// The tracker's business rules, ported from the prototype so the app, worker and tests share one copy.
import type { Crane, Drawings, Item, Job, Stage, Status, Task, Tracking } from './types';
import { daysUntil, fmtDate, addDays, weekday } from './dates';
import { baseQuote, compareQuotes } from './quote';

export const STATUSES: [Status, string][] = [
  ['not-started', 'Not started'], ['drawings', 'Drawings'], ['fabrication', 'Fabrication'],
  ['ready', 'Ready to install'], ['installing', 'Installing'], ['complete', 'Complete'], ['hold', 'On hold']];
export const STATUS_LABEL: Record<string, string> = Object.fromEntries(STATUSES);
export const DRAWINGS: Drawings[] = ['Not started', 'In progress', 'Sent for approval', 'Approved'];
export const CRANE: Crane[] = ['Not booked', 'Booked', 'Not needed'];
export const WAITING: [string, string][] = [['', 'Nobody'], ['PM', 'PM'], ['Builder', 'Builder'], ['Detailers', 'Detailers']];
export const SOON_DAYS = 14;

/** Tracker defaults for a newly added job or stage. */
export const NEW_TRACKING = { status: 'not-started', drawings: 'Not started', crane: 'Not booked' } as const;

type Trackable = Pick<Tracking, 'status' | 'completed_date' | 'install_date' | 'crane'> & { staged?: boolean; isStage?: boolean };

export const isDone = (j: Pick<Tracking, 'status' | 'completed_date'>): boolean => j.status === 'complete' || !!j.completed_date;

/** Installs within 14 days with no crane booked. A staged parent never flags; its stages do. */
export function craneFlag(j: Trackable, today: string): boolean {
  if (j.staged && !j.isStage) return false;
  const d = daysUntil(j.install_date, today);
  return d !== null && d >= 0 && d <= SOON_DAYS && j.crane !== 'Booked' && j.crane !== 'Not needed' && !isDone(j);
}

export const craneOk = (j: Pick<Tracking, 'crane'>): boolean => j.crane === 'Booked' || j.crane === 'Not needed';

// ---------- Stages ----------

/** A stage dressed as a job card: it borrows its parent's PM, address and builder, and carries no money. */
export function stageView(st: Stage, parent: Job | undefined): Item {
  return {
    ...st,
    quote: st.id, id: st.id, isStage: true, ref: st.parent_quote, type: 'Stage',
    pm: parent ? parent.pm : '',
    address: (parent ? parent.address : st.parent_quote) + ' · ' + (st.name || 'Unnamed stage'),
    builder: parent ? parent.builder : '',
    remaining: 0, quoted: 0, invoiced: 0, quote_date: null, handed_date: null, staged: false,
  };
}

export function stagesOf(stages: Stage[], jobs: Job[], quote: string): Item[] {
  const parent = jobs.find((x) => x.quote === quote);
  return stages.filter((x) => x.parent_quote === quote)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    .map((st) => stageView(st, parent));
}

/** Every stage of every job that has its Stages switch on. */
export function stageItems(stages: Stage[], jobs: Job[]): Item[] {
  return jobs.filter((j) => j.staged).flatMap((j) => stagesOf(stages, jobs, j.quote));
}

export function nextStageOrder(stages: Stage[], parent: string): number {
  return Math.max(0, ...stages.filter((x) => x.parent_quote === parent).map((x) => x.sort_order || 0)) + 1;
}

// ---------- Editing side-effects ----------

export type FieldKey = 'status' | 'handed_date' | 'drawings' | 'draw_waiting' | 'drawings_updated' | 'fab_date' | 'install_date'
  | 'crane' | 'days_onsite' | 'completed_date' | 'notes' | 'name';
export type FieldKind = 'select' | 'date' | 'number' | 'text' | 'textarea';
export type Patch = Partial<Record<FieldKey | 'prev_status' | 'hold_since' | 'pm' | 'staged', unknown>>;

/**
 * What to save when someone changes one field. Mirrors the prototype exactly:
 * drawings changes stamp Last updated, Approved clears Waiting on, completed date and status move together,
 * and leaving hold clears its bookkeeping.
 */
export function fieldPatch(item: Partial<Tracking>, key: FieldKey, raw: string, kind: FieldKind, today: string): Patch {
  let v: unknown = raw;
  if (kind === 'number') v = raw === '' ? null : Math.max(0, Math.round(Number(raw)));
  if (kind === 'textarea') v = raw.trim();
  if (v === '' || (typeof v === 'number' && Number.isNaN(v))) v = key === 'name' ? '' : null; // stage names can be blank, not null
  const cur = item as Record<string, unknown>;
  const patch: Patch = { [key]: v };
  if ((key === 'drawings' || key === 'draw_waiting') && String(cur[key] || '') !== String(v || '')) patch.drawings_updated = today;
  if (key === 'drawings' && v === 'Approved') patch.draw_waiting = null;
  if (key === 'completed_date' && v) patch.status = 'complete';
  if (key === 'completed_date' && !v && item.status === 'complete') patch.status = 'installing';
  if (key === 'status' && v === 'complete' && !item.completed_date) patch.completed_date = today;
  if (key === 'status' && v === 'hold' && item.status !== 'hold') { patch.prev_status = item.status || 'not-started'; patch.hold_since = today; }
  if (key === 'status' && v !== 'hold' && item.status === 'hold') { patch.prev_status = null; patch.hold_since = null; }
  if (key === 'status' && v !== 'complete' && item.completed_date) patch.completed_date = null;
  return patch;
}

/** The On hold switch. On: remember the status and pause. Off: go back to it. */
export function holdPatch(item: Partial<Tracking>, today: string): Patch {
  if (item.status === 'hold') {
    return { status: item.prev_status && item.prev_status !== 'hold' ? item.prev_status : 'not-started', prev_status: null, hold_since: null };
  }
  return { prev_status: item.status || 'not-started', status: 'hold', hold_since: today };
}

// ---------- Jobs list ----------

export type FilterKey = '' | Status | 'crane' | 'nohand';
export function filters(today: string): [FilterKey, string, (j: Item) => boolean][] {
  return [
    ['', 'All', () => true],
    ...STATUSES.map(([k, l]): [FilterKey, string, (j: Item) => boolean] => [k, l, (j) => (j.status || 'not-started') === k]),
    ['crane', 'Crane not booked', (j) => craneFlag(j, today)],
    ['nohand', 'No handover date', (j) => !j.handed_date && !isDone(j)],
  ];
}

export function matchesSearch(j: Item, q: string, withNotes: boolean): boolean {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  return (j.quote + ' ' + j.address + ' ' + j.builder + (withNotes ? ' ' + (j.notes || '') : '')).toLowerCase().includes(s);
}

/** Which quote a row is filed under: a variation goes inside its parent when the parent is in the list. */
function parentKey(j: Item, present: Map<string, Item>): string {
  return j.type === 'Variation' && present.has(baseQuote(j.quote)) ? baseQuote(j.quote) : j.quote;
}

/** A job counts as completed when it and all its variations are complete. */
export function groupDoneMap(vis: Item[]): Map<string, boolean> {
  const byQ = new Map(vis.map((j) => [j.quote, j]));
  const m = new Map<string, Item[]>();
  vis.forEach((j) => { const k = parentKey(j, byQ); if (!m.has(k)) m.set(k, []); m.get(k)!.push(j); });
  const out = new Map<string, boolean>();
  m.forEach((L) => { const d = L.every(isDone); L.forEach((j) => out.set(j.quote, d)); });
  return out;
}

export interface JobGroup { main: Item; vars: Item[]; mainHit: boolean; varHit: boolean; flag: boolean }

/**
 * Group variations under their parent, filter, and sort as the prototype does:
 * crane-flagged first, then by install date, then newest quote. Completed view: newest completion first.
 */
export function buildJobGroups(base: Item[], view: 'active' | 'done', fn: (j: Item) => boolean, today: string): JobGroup[] {
  const byQuote = new Map(base.map((j) => [j.quote, j]));
  const groups = new Map<string, { main: Item; vars: Item[] }>();
  base.forEach((j) => {
    const parent = parentKey(j, byQuote);
    if (!groups.has(parent)) groups.set(parent, { main: byQuote.get(parent)!, vars: [] });
    if (parent !== j.quote) groups.get(parent)!.vars.push(j);
  });
  const shown = [...groups.values()].map((g) => {
    g.vars.sort((a, b) => compareQuotes(a.quote, b.quote));
    return { ...g, mainHit: fn(g.main), varHit: g.vars.some(fn), flag: craneFlag(g.main, today) || g.vars.some((v) => craneFlag(v, today)) };
  }).filter((g) => g.mainHit || g.varHit);
  const date = (j: Item) => (j.quote_date ? new Date(j.quote_date).getTime() : 0);
  // Ties (same date) go newest quote number first, so the order never depends on load order.
  const newestQuote = (a: JobGroup, b: JobGroup) => compareQuotes(b.main.quote, a.main.quote);
  if (view === 'done') {
    return shown.sort((a, b) => ((b.main.completed_date || '') > (a.main.completed_date || '') ? 1 : (b.main.completed_date || '') < (a.main.completed_date || '') ? -1 : 0) || newestQuote(a, b));
  }
  return shown.sort((a, b) =>
    (Number(b.flag) - Number(a.flag)) ||
    ((a.main.install_date || '9999') < (b.main.install_date || '9999') ? -1 : (a.main.install_date || '9999') > (b.main.install_date || '9999') ? 1 : 0) ||
    (date(b.main) - date(a.main)) ||
    newestQuote(a, b));
}

// ---------- Calendar ----------

/** Install spans count weekdays only: start on the install date (a weekend start moves to Monday), run for days onsite. Blank = 1 day. */
export function installDays(j: Pick<Tracking, 'install_date' | 'days_onsite'>): string[] {
  if (!j.install_date) return [];
  const n = Math.max(1, Number(j.days_onsite) || 1);
  const out: string[] = [];
  let d = j.install_date.slice(0, 10);
  while (out.length < n) { const w = weekday(d); if (w !== 0 && w !== 6) out.push(d); d = addDays(d, 1); }
  return out;
}

// ---------- To do ----------

export type StepKey = 'handover' | 'draw-start' | 'draw-send' | 'draw-chase' | 'fab' | 'install' | 'days' | 'crane';
export const NEXT_STEPS: [StepKey, string, string][] = [
  ['handover', 'Record the date this job was handed to you', 'Handover date needed'],
  ['draw-start', 'Start shop drawings', 'Start drawings'],
  ['draw-send', 'Finish drawings and send for approval', 'Finish and send drawings'],
  ['draw-chase', 'Chase drawing approval', 'Chase drawing approval'],
  ['fab', 'Hand to fabrication', 'Hand to fabrication'],
  ['install', 'Set the install date', 'Set install date'],
  ['days', 'Set days allowed onsite', 'Set days onsite'],
  ['crane', 'Book crane', 'Book crane'],
];

export interface AutoTask { auto: true; text: string; level: 'urgent' | 'next'; stage?: StepKey; quote: string; pm: string; job: Item }

/** The reminders the tracker raises for one job or stage. Held and completed items raise none. */
export function autoTasks(j: Item, today: string): AutoTask[] {
  if (isDone(j) || j.status === 'hold') return [];
  // A staged job only needs its handover date; each stage carries the rest. Stages share the job's handover date.
  if (j.staged && !j.isStage) {
    return j.handed_date ? [] : [{ auto: true, text: NEXT_STEPS[0][1], level: 'next', stage: 'handover', quote: j.quote, pm: j.pm, job: j }];
  }
  const out: AutoTask[] = [];
  const d = daysUntil(j.install_date, today);
  const dd = (n: number) => n + (n === 1 ? ' day' : ' days');
  const mk = (text: string, level: 'urgent' | 'next', stage?: StepKey) =>
    out.push({ auto: true, text: (j.isStage ? (j.name || 'Stage') + ': ' : '') + text, level, stage, quote: j.ref || j.quote, pm: j.pm, job: j });
  const soon = j.install_date && d !== null && d >= 0 && d <= SOON_DAYS;
  if (craneFlag(j, today)) mk('Book crane, install in ' + dd(d!), 'urgent');
  if (soon && j.drawings !== 'Approved') mk('Drawings not approved, install in ' + dd(d!), 'urgent');
  if (soon && !j.fab_date) mk('Not in fabrication yet, install in ' + dd(d!), 'urgent');
  if (j.install_date && d !== null && d < 0 && j.status !== 'installing') mk('Install date has passed. Update the status or move the date', 'urgent');
  let stage: StepKey | null = null;
  if (!j.handed_date && !j.isStage) stage = 'handover';
  else if (!j.drawings || j.drawings === 'Not started') stage = 'draw-start';
  else if (j.drawings === 'In progress') stage = 'draw-send';
  else if (j.drawings === 'Sent for approval') stage = 'draw-chase';
  else if (!j.fab_date) stage = 'fab';
  else if (!j.install_date) stage = 'install';
  else if (!j.days_onsite) stage = 'days';
  else if (!craneOk(j) && !craneFlag(j, today)) stage = 'crane';
  if (stage) {
    const st = NEXT_STEPS.find((x) => x[0] === stage)!;
    mk(st[1] + (stage === 'crane' ? ' for ' + fmtDate(j.install_date) : ''), 'next', stage);
  }
  return out;
}

export const lateTask = (t: Pick<Task, 'done' | 'due'> & { auto?: boolean }, today: string): boolean =>
  !t.auto && !t.done && !!t.due && t.due < today;
